export interface CursorEffectDefinition {
  id: string;
  label: string;
  description: string;
  previewClass: string;
}

export const cursorEffectDefinitions: CursorEffectDefinition[] = [
  { id: 'ring', label: 'Eleganter Ring', description: 'Kleiner Punkt & glatter Kreis – reagiert auf klickbare Elemente', previewClass: 'vp-cursor-preview-ring' },
  { id: 'blob', label: 'Flüssiger Blob', description: 'Weicher Blob der Form wechselt – träge und lebendig', previewClass: 'vp-cursor-preview-blob' },
  { id: 'trail', label: 'Kometen-Schweif', description: 'Leuchtende Partikelkette die dem Cursor folgt', previewClass: 'vp-cursor-preview-trail' },
  { id: 'spotlight', label: 'Spotlight', description: 'Taschenlampen-Effekt – beleuchtet den Inhalt unter dem Cursor', previewClass: 'vp-cursor-preview-spotlight' },
  { id: 'sparks', label: 'Funken', description: 'Glitzernde Funken die beim Bewegen entstehen und verschwinden', previewClass: 'vp-cursor-preview-sparks' },
];

type CleanupFn = () => void;
let activeCleanup: CleanupFn | null = null;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const hexToRgb = (hex: string): string => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

const mk = (css: string, tag = 'div'): HTMLElement => {
  const el = document.createElement(tag);
  el.setAttribute('data-vp-cursor', '1');
  Object.assign(el.style, { pointerEvents: 'none', position: 'fixed', zIndex: '99999', ...parseCss(css) });
  return el;
};

const parseCss = (css: string): Record<string, string> =>
  Object.fromEntries(
    css.split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => {
        const i = s.indexOf(':');
        const prop = s.slice(0, i).trim().replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
        return [prop, s.slice(i + 1).trim()];
      }),
  );

const removeAll = () => document.querySelectorAll('[data-vp-cursor]').forEach(el => el.remove());

// ── Ring ─────────────────────────────────────────────────────────────────────

const ringEffect = (color: string): CleanupFn => {
  const rgb = hexToRgb(color);
  document.body.style.cursor = 'none';

  const dot = mk(`width:8px; height:8px; background:${color}; border-radius:50%; transform:translate(-50%,-50%); top:0; left:0`);
  const ring = mk(`
    width:36px; height:36px;
    border:2px solid rgba(${rgb},0.75);
    border-radius:50%;
    transform:translate(-50%,-50%);
    top:0; left:0;
    transition: width .25s ease, height .25s ease, border-color .25s ease, opacity .25s ease;
  `);
  document.body.append(dot, ring);

  let tx = 0, ty = 0, rx = 0, ry = 0, raf = 0;

  const onMove = (e: MouseEvent) => {
    tx = e.clientX; ty = e.clientY;
    dot.style.left = tx + 'px'; dot.style.top = ty + 'px';
  };

  const onOver = (e: Event) => {
    if ((e.target as HTMLElement).closest?.('a,button,[role="button"],label,input,select,textarea')) {
      ring.style.width = '48px'; ring.style.height = '48px';
      ring.style.borderColor = color;
    }
  };
  const onOut = (e: Event) => {
    if ((e.target as HTMLElement).closest?.('a,button,[role="button"],label,input,select,textarea')) {
      ring.style.width = '36px'; ring.style.height = '36px';
      ring.style.borderColor = `rgba(${rgb},0.75)`;
    }
  };

  const tick = () => {
    rx = lerp(rx, tx, 0.14); ry = lerp(ry, ty, 0.14);
    ring.style.left = rx + 'px'; ring.style.top = ry + 'px';
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseover', onOver);
  document.addEventListener('mouseout', onOut);

  return () => {
    cancelAnimationFrame(raf);
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseover', onOver);
    document.removeEventListener('mouseout', onOut);
    removeAll();
    document.body.style.cursor = '';
  };
};

// ── Blob ─────────────────────────────────────────────────────────────────────

const blobEffect = (color: string): CleanupFn => {
  const rgb = hexToRgb(color);
  document.body.style.cursor = 'none';

  const style = document.createElement('style');
  style.setAttribute('data-vp-cursor', '1');
  style.textContent = `
    @keyframes vp-blob-morph {
      0%   { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
      25%  { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
      50%  { border-radius: 50% 60% 30% 70% / 40% 50% 60% 50%; }
      75%  { border-radius: 70% 30% 50% 50% / 30% 60% 40% 70%; }
      100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
    }
  `;
  document.head.appendChild(style);

  const blob = mk(`
    width:46px; height:46px;
    background: rgba(${rgb}, 0.18);
    border: 2px solid rgba(${rgb}, 0.55);
    animation: vp-blob-morph 4s ease-in-out infinite;
    transform: translate(-50%,-50%);
    top:0; left:0;
    transition: width .28s ease, height .28s ease;
    backdrop-filter: blur(1px);
  `);
  const dot = mk(`
    width:7px; height:7px;
    background:${color};
    border-radius:50%;
    transform:translate(-50%,-50%);
    top:0; left:0;
  `);
  document.body.append(blob, dot);

  let tx = 0, ty = 0, bx = 0, by = 0, raf = 0;

  const onMove = (e: MouseEvent) => {
    tx = e.clientX; ty = e.clientY;
    dot.style.left = tx + 'px'; dot.style.top = ty + 'px';
  };
  const onOver = (e: Event) => {
    if ((e.target as HTMLElement).closest?.('a,button,[role="button"]')) {
      blob.style.width = '62px'; blob.style.height = '62px';
    }
  };
  const onOut = (e: Event) => {
    if ((e.target as HTMLElement).closest?.('a,button,[role="button"]')) {
      blob.style.width = '46px'; blob.style.height = '46px';
    }
  };

  const tick = () => {
    bx = lerp(bx, tx, 0.09); by = lerp(by, ty, 0.09);
    blob.style.left = bx + 'px'; blob.style.top = by + 'px';
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseover', onOver);
  document.addEventListener('mouseout', onOut);

  return () => {
    cancelAnimationFrame(raf);
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseover', onOver);
    document.removeEventListener('mouseout', onOut);
    removeAll();
    document.body.style.cursor = '';
  };
};

// ── Trail ────────────────────────────────────────────────────────────────────

const trailEffect = (color: string): CleanupFn => {
  const rgb = hexToRgb(color);
  document.body.style.cursor = 'none';

  const COUNT = 14;
  const dots = Array.from({ length: COUNT }, (_, i) => {
    const t = i / (COUNT - 1);
    const size = lerp(10, 3, t);
    const opacity = lerp(1, 0.08, t);
    const el = mk(`
      width:${size}px; height:${size}px;
      background: rgba(${rgb}, ${opacity});
      border-radius:50%;
      transform:translate(-50%,-50%);
      top:0; left:0;
      will-change: left, top;
    `);
    document.body.appendChild(el);
    return el;
  });

  const pos = Array.from({ length: COUNT }, () => ({ x: 0, y: 0 }));
  let mx = 0, my = 0, raf = 0;

  const onMove = (e: MouseEvent) => { mx = e.clientX; my = e.clientY; };

  const tick = () => {
    pos[0]!.x = lerp(pos[0]!.x, mx, 0.38);
    pos[0]!.y = lerp(pos[0]!.y, my, 0.38);
    for (let i = 1; i < COUNT; i++) {
      pos[i]!.x = lerp(pos[i]!.x, pos[i - 1]!.x, 0.38);
      pos[i]!.y = lerp(pos[i]!.y, pos[i - 1]!.y, 0.38);
    }
    dots.forEach((dot, i) => {
      dot.style.left = pos[i]!.x + 'px';
      dot.style.top = pos[i]!.y + 'px';
    });
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  document.addEventListener('mousemove', onMove);

  return () => {
    cancelAnimationFrame(raf);
    document.removeEventListener('mousemove', onMove);
    removeAll();
    document.body.style.cursor = '';
  };
};

// ── Spotlight ────────────────────────────────────────────────────────────────

const spotlightEffect = (color: string): CleanupFn => {
  const rgb = hexToRgb(color);

  const overlay = mk(`
    inset:0; width:100%; height:100%;
    background: radial-gradient(circle 120px at 50% 50%, transparent 0%, transparent 50%, rgba(0,0,0,0.55) 100%);
    transition: background 0.06s linear;
    z-index: 9998;
  `);
  document.body.appendChild(overlay);

  const glow = mk(`
    width:24px; height:24px;
    background: rgba(${rgb},0.7);
    border-radius:50%;
    transform:translate(-50%,-50%);
    filter: blur(8px);
    top:0; left:0;
    z-index: 9999;
  `);
  document.body.appendChild(glow);

  const onMove = (e: MouseEvent) => {
    const xPct = (e.clientX / window.innerWidth) * 100;
    const yPct = (e.clientY / window.innerHeight) * 100;
    overlay.style.background = `radial-gradient(circle 130px at ${xPct}% ${yPct}%, transparent 0%, transparent 45%, rgba(0,0,0,0.58) 100%)`;
    glow.style.left = e.clientX + 'px';
    glow.style.top = e.clientY + 'px';
  };

  document.addEventListener('mousemove', onMove);

  return () => {
    document.removeEventListener('mousemove', onMove);
    removeAll();
  };
};

// ── Sparks ───────────────────────────────────────────────────────────────────

const sparksEffect = (color: string): CleanupFn => {
  const rgb = hexToRgb(color);
  document.body.style.cursor = 'none';

  const style = document.createElement('style');
  style.setAttribute('data-vp-cursor', '1');
  style.textContent = `
    @keyframes vp-spark-fly {
      0%   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0); }
    }
  `;
  document.head.appendChild(style);

  const dot = mk(`
    width:8px; height:8px;
    background:${color};
    border-radius:50%;
    transform:translate(-50%,-50%);
    top:0; left:0;
    box-shadow: 0 0 6px rgba(${rgb}, 0.8);
  `);
  document.body.appendChild(dot);

  let lastSpawnTime = 0;
  let mx = 0, my = 0;

  const spawnSpark = (x: number, y: number) => {
    const spark = document.createElement('div');
    spark.setAttribute('data-vp-cursor', '1');
    const angle = Math.random() * Math.PI * 2;
    const dist = 18 + Math.random() * 28;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const size = 2 + Math.random() * 4;
    const dur = 0.45 + Math.random() * 0.35;
    spark.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 99999;
      width: ${size}px;
      height: ${size}px;
      background: rgba(${rgb}, ${0.7 + Math.random() * 0.3});
      border-radius: 50%;
      left: ${x}px;
      top: ${y}px;
      --dx: ${dx}px;
      --dy: ${dy}px;
      animation: vp-spark-fly ${dur}s ease-out forwards;
    `;
    document.body.appendChild(spark);
    setTimeout(() => spark.remove(), dur * 1000 + 50);
  };

  const onMove = (e: MouseEvent) => {
    mx = e.clientX; my = e.clientY;
    dot.style.left = mx + 'px'; dot.style.top = my + 'px';
    const now = performance.now();
    if (now - lastSpawnTime > 35) {
      lastSpawnTime = now;
      const count = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) spawnSpark(mx, my);
    }
  };

  document.addEventListener('mousemove', onMove);

  return () => {
    document.removeEventListener('mousemove', onMove);
    removeAll();
    document.body.style.cursor = '';
  };
};

// ── Public API ────────────────────────────────────────────────────────────────

const effectMap: Record<string, (color: string) => CleanupFn> = {
  ring: ringEffect,
  blob: blobEffect,
  trail: trailEffect,
  spotlight: spotlightEffect,
  sparks: sparksEffect,
};

export const applyCursorEffect = (effectId: string | undefined, color = '#48c5ce') => {
  if (typeof document === 'undefined') return;
  activeCleanup?.();
  activeCleanup = null;
  document.body.style.cursor = '';

  const fn = effectId ? effectMap[effectId] : undefined;
  if (fn) activeCleanup = fn(color);
};

export const removeCursorEffect = () => {
  if (typeof document === 'undefined') return;
  activeCleanup?.();
  activeCleanup = null;
  document.body.style.cursor = '';
};
